import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export const AddEmployees = ({
  employee,
  editEmployee,
  open,
  handleCancel,
  handleAddEmployee,
  handleUpdateEmployee,
  locations = [],
  employmentStatuses = [],
  previousEmploymentStatusId = null,
  originalHadCredentials = false,
  onSearchLastname = null,
  onViewMatch = null,
}) => {
  const [newEmployee, setNewEmployee] = useState(employee);
  const [employmentStatusChangeDate, setEmploymentStatusChangeDate] = useState(
    new Date()
  );
  // { field: 'name' | 'coins' | 'general', message } — inline submit errors
  const [submitError, setSubmitError] = useState(null);
  // Possible duplicate matches surfaced when entering a last name (Add only)
  const [possibleMatches, setPossibleMatches] = useState([]);
  const [matchesDismissed, setMatchesDismissed] = useState(false);

  useEffect(() => {
    if (employee) {
      setNewEmployee(employee);
      setEmploymentStatusChangeDate(new Date());
    }
    // Reset transient UI whenever the modal is (re)opened for a record
    setSubmitError(null);
    setPossibleMatches([]);
    setMatchesDismissed(false);
  }, [employee]);

  const localUnions = [
    'E124VD',
    'E124Z1',
    'E226',
    'E226VD',
    'E584',
    'E584VD',
    'M1529',
    'P008',
    'P4412B',
    'P533',
  ];

  const handleChange = (e) => {
    e.preventDefault();
    const itemChanged = e.target.name;
    const valueChanged = [
      'is_admin',
      'is_timecard',
      'is_workflow',
      'is_analytics',
    ].includes(itemChanged)
      ? e.target.checked
      : e.target.value;
    const changeEmployee = { ...newEmployee, [itemChanged]: valueChanged };
    setNewEmployee(changeEmployee);
    // Clear a stale submit error once the relevant field is edited
    if (submitError) {
      if (
        (submitError.field === 'coins' && itemChanged === 'coins_empl') ||
        (submitError.field === 'email' && itemChanged === 'email') ||
        (submitError.field === 'name' &&
          (itemChanged === 'firstname' || itemChanged === 'lastname')) ||
        submitError.field === 'general'
      ) {
        setSubmitError(null);
      }
    }
  };

  // On the Add form, surface existing employees with the same last name so the
  // admin can reactivate rather than create a duplicate. Fires on field blur.
  const handleLastnameBlur = async () => {
    if (editEmployee || !onSearchLastname) return;
    const lastname = (newEmployee.lastname || '').trim();
    if (!lastname) {
      setPossibleMatches([]);
      return;
    }
    try {
      const matches = await onSearchLastname(lastname);
      setPossibleMatches(Array.isArray(matches) ? matches : []);
      setMatchesDismissed(false);
    } catch (err) {
      // A failed lookup should never block adding an employee
      setPossibleMatches([]);
    }
  };

  // Rank matches client-side: exact first-name match first, then first names
  // starting with the same text, then the rest. Backend already sorts
  // active-first, which we preserve as the tie-breaker via a stable sort.
  const rankedMatches = () => {
    const fn = (newEmployee.firstname || '').trim().toLowerCase();
    const tier = (m) => {
      const mf = (m.firstname || '').toLowerCase();
      if (fn && mf === fn) return 0;
      if (fn && mf.startsWith(fn)) return 1;
      return 2;
    };
    return [...possibleMatches].sort((a, b) => tier(a) - tier(b));
  };

  const blankEmployee = {
    firstname: '',
    lastname: '',
    email: '',
    coins_empl: '',
    home_union: '',
    is_admin: false,
    is_timecard: false,
    is_workflow: false,
    is_analytics: false,
    location_id: '',
    employment_status_id: 'active',
  };

  const handleClose = () => {
    setNewEmployee(blankEmployee);
    setSubmitError(null);
    setPossibleMatches([]);
    setMatchesDismissed(false);
    handleCancel();
  };

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const wantsLogin =
      newEmployee.is_admin ||
      newEmployee.is_timecard ||
      newEmployee.is_workflow ||
      newEmployee.is_analytics;

    if (wantsLogin && !newEmployee.email) {
      alert(
        'Email is required when any login permission (Admin, Timecard, Workflow, or Analytics) is selected.'
      );
      return;
    }

    // Validation: If email is provided, it must be valid format
    if (newEmployee.email && !isValidEmail(newEmployee.email)) {
      alert('Please enter a valid email address');
      return;
    }

    // Only block when this save would CREATE new login credentials.
    // Editing an employee who already has credentials (e.g. laying them
    // off while their permission boxes stay checked) must be allowed —
    // that deactivates their login rather than creating one.
    const isCreatingCredentials =
      wantsLogin && (!editEmployee || !originalHadCredentials);
    if (
      isCreatingCredentials &&
      newEmployee.employment_status_id !== 'active'
    ) {
      alert('Cannot create login credentials for non-active employees');
      return;
    }

    // Build the payload (include status change date when status changed on edit)
    const statusChanged =
      editEmployee &&
      previousEmploymentStatusId &&
      previousEmploymentStatusId !==
        (newEmployee.employment_status_id || 'active');
    const payload = statusChanged
      ? { ...newEmployee, status_change_date: employmentStatusChangeDate }
      : newEmployee;

    try {
      if (editEmployee) {
        await handleUpdateEmployee(payload);
      } else {
        await handleAddEmployee(payload);
      }
      // Success — the parent closed the modal; reset the form for next time.
      setNewEmployee(blankEmployee);
      setPossibleMatches([]);
      setMatchesDismissed(false);
    } catch (err) {
      // Map backend 409 conflicts to inline errors; keep the modal open and
      // do not clear the form so the admin can correct the input.
      const apiError = err?.response?.data?.error;
      const message = err?.response?.data?.message;
      if (apiError?.reason === 'duplicate_coinsid') {
        setSubmitError({ field: 'coins', message });
      } else if (apiError?.reason === 'duplicate_email') {
        setSubmitError({ field: 'email', message });
      } else if (apiError?.reason === 'duplicate_employee_name') {
        setSubmitError({ field: 'name', message });
      } else {
        setSubmitError({
          field: 'general',
          message: message || 'There was an error saving the employee.',
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': { maxWidth: '640px' } }}
    >
      <DialogTitle>
        {editEmployee ? 'Edit Employee' : 'New Employee'}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          {(submitError?.field === 'name' ||
            submitError?.field === 'general') && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {submitError.message}
            </Alert>
          )}
          <TextField
            required
            autoFocus
            fullWidth
            margin="normal"
            id="firstname"
            label="First Name"
            name="firstname"
            autoComplete="off"
            type="text"
            value={newEmployee.firstname}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            margin="normal"
            id="lastname"
            label="Last Name"
            name="lastname"
            autoComplete="off"
            type="text"
            value={newEmployee.lastname}
            onChange={handleChange}
            onBlur={handleLastnameBlur}
          />
          {!editEmployee && !matchesDismissed && possibleMatches.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                We found employees with this last name — is this person already
                in the system?
              </Typography>
              <Box
                sx={
                  possibleMatches.length > 8
                    ? { maxHeight: 288, overflowY: 'auto', pr: 0.5 }
                    : {}
                }
              >
                {rankedMatches().map((m) => (
                  <Box
                    key={m.employee_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 160 }}>
                      {m.firstname} {m.lastname}
                    </Typography>
                    <Chip
                      size="small"
                      label={m.employment_status_name || m.employment_status_id}
                      color={
                        (m.employment_status_id || '').toLowerCase() ===
                        'active'
                          ? 'success'
                          : 'default'
                      }
                    />
                    {m.coins_empl && (
                      <Typography variant="caption" color="text.secondary">
                        COINS: {m.coins_empl}
                      </Typography>
                    )}
                    {onViewMatch && (
                      <Button
                        size="small"
                        sx={{ ml: 'auto' }}
                        onClick={() => onViewMatch(m.employee_id)}
                      >
                        View
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => setMatchesDismissed(true)}
              >
                None of these — continue
              </Button>
            </Alert>
          )}
          {(newEmployee.is_admin ||
            newEmployee.is_timecard ||
            newEmployee.is_workflow ||
            newEmployee.is_analytics) && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <TextField
                required
                fullWidth
                margin="normal"
                id="email"
                label="Email"
                name="email"
                autoComplete="off"
                type="email"
                value={newEmployee.email}
                onChange={handleChange}
                error={submitError?.field === 'email'}
                helperText="Required for system login."
              />
              {submitError?.field === 'email' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {submitError.message}
                </Alert>
              )}
              {!employee?.email && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Default password will be assigned to the user.
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    User will be required to change password on first login.
                  </Typography>
                </>
              )}
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 2,
              color: 'text.secondary',
            }}
          >
            <FormControl sx={{ width: '30%' }} margin="normal">
              <InputLabel id="location-select-label">Location</InputLabel>
              <Select
                labelId="location-select-label"
                id="location_id"
                name="location_id"
                value={newEmployee.location_id || ''}
                label="Location"
                onChange={handleChange}
              >
                {locations && locations.length > 0 ? (
                  locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No locations available</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl
              sx={{ width: '30%' }}
              // fullWidth
            >
              <Autocomplete
                id="home_union"
                name="home_union"
                onChange={(event, value) => {
                  setNewEmployee({ ...newEmployee, home_union: value });
                }}
                value={newEmployee.home_union}
                inputValue={newEmployee.home_union}
                onInputChange={(event, newInputValue) => {
                  setNewEmployee({ ...newEmployee, home_union: newInputValue });
                }}
                freeSolo
                options={localUnions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="home_union"
                    margin="normal"
                    width="80px"
                    label="Home Union"
                  />
                )}
              />
            </FormControl>
            <TextField
              margin="normal"
              sx={{ width: '30%' }}
              id="coins_empl"
              label="COINS Id"
              name="coins_empl"
              autoComplete="off"
              type="text"
              value={newEmployee.coins_empl}
              onChange={handleChange}
              error={submitError?.field === 'coins'}
            />
          </Box>
          {submitError?.field === 'coins' && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {submitError.message}
            </Alert>
          )}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 2,
              color: 'text.secondary',
            }}
          >
            <FormControl sx={{ width: '30%' }} margin="normal">
              <InputLabel id="employment-status-label">
                Employment Status
              </InputLabel>
              <Select
                labelId="employment-status-label"
                id="employment_status_id"
                name="employment_status_id"
                value={newEmployee.employment_status_id || 'active'}
                label="Employment Status"
                onChange={handleChange}
              >
                {employmentStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {editEmployee &&
            previousEmploymentStatusId &&
            previousEmploymentStatusId !==
              (newEmployee.employment_status_id || 'active') && (
              <Box sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Effective Date"
                    value={employmentStatusChangeDate}
                    onChange={(newValue) => {
                      setEmploymentStatusChangeDate(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            )}
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Login permissions
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: { xs: 0.5, sm: 1 },
                rowGap: 0.5,
                width: '100%',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={newEmployee.is_admin}
                  />
                }
                label="Administrator"
                labelPlacement="end"
                id="is_admin"
                name="is_admin"
                sx={{ flexShrink: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={newEmployee.is_timecard}
                  />
                }
                label="Timecard access"
                labelPlacement="end"
                id="is_timecard"
                name="is_timecard"
                sx={{ flexShrink: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={newEmployee.is_workflow}
                  />
                }
                label="Workflow access"
                labelPlacement="end"
                id="is_workflow"
                name="is_workflow"
                sx={{ flexShrink: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={newEmployee.is_analytics}
                  />
                }
                label="Analytics access"
                labelPlacement="end"
                id="is_analytics"
                name="is_analytics"
                sx={{ flexShrink: 0 }}
              />
            </Box>
            <FormHelperText sx={{ ml: 0, mt: 0.5, mr: 0 }}>
              Admins can access the Timecards tab without Timecard access, but
              they only appear in the Foreman dropdown when Timecard access is
              checked as well.
            </FormHelperText>
          </Box>
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
              sx={{
                width: '25%',
                whiteSpace: 'nowrap',
                minHeight: '36px',
              }}
            >
              {editEmployee ? 'Update Employee' : 'Add Employee'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                width: '25%',
                whiteSpace: 'nowrap',
                minHeight: '36px',
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
